import { Component } from '@angular/core';
import {
  DealerHealthResponse,
  DealerHealthRow,
} from 'src/app/models/dealerHealth';
import { AdminService } from 'src/app/services/admin-service';

@Component({
  selector: 'pla-admin-dealer-health',
  templateUrl: './admin-dealer-health.component.html',
  styleUrls: ['./admin-dealer-health.component.scss'],
})
export class AdminDealerHealthComponent {
  public response: DealerHealthResponse | null = null;
  public dealers: DealerHealthRow[] = [];
  public loading: boolean = false;
  public errored: boolean = false;
  public showUnmonitored: boolean = false;

  public coverageLoading: boolean = false;
  public coverageError: string | null = null;

  constructor(private adminService: AdminService) {
    this.load(false);
  }

  public load(refresh: boolean): void {
    this.loading = true;
    this.errored = false;
    this.adminService.getDealerHealth(refresh).subscribe({
      next: (resp) => {
        this.response = resp;
        this.applyFilter();
        this.loading = false;
      },
      error: () => {
        this.response = null;
        this.dealers = [];
        this.loading = false;
        this.errored = true;
      },
    });
  }

  // Explicit admin action: computes the sole/shared coverage snapshot on the
  // server (may take up to two minutes) and caches it with an asOf timestamp.
  // Read-only. On failure the previous data stays on screen.
  public loadCoverage(): void {
    if (this.coverageLoading) return;
    this.coverageLoading = true;
    this.coverageError = null;
    this.adminService.getDealerHealth(false, true).subscribe({
      next: (resp) => {
        this.response = resp;
        this.applyFilter();
        this.coverageLoading = false;
      },
      error: () => {
        this.coverageError =
          'Coverage calculation failed — showing the previous data.';
        this.coverageLoading = false;
      },
    });
  }

  public toggleUnmonitored(): void {
    this.showUnmonitored = !this.showUnmonitored;
    this.applyFilter();
  }

  private applyFilter(): void {
    const all = (this.response && this.response.dealers) || [];
    this.dealers = this.showUnmonitored
      ? all
      : all.filter((d) => d.active && d.scrapeConfigured && d.scrapeActive);
  }

  public statusBadgeClass(status: string): string {
    switch (status) {
      case 'Ok':
        return 'bg-success';
      case 'Warning':
        return 'bg-warning text-dark';
      case 'Broken':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  public overallBadgeClass(overall: string): string {
    switch (overall) {
      case 'Healthy':
        return 'bg-success';
      case 'Warning':
        return 'bg-warning text-dark';
      case 'Broken':
        return 'bg-danger';
      case 'InsufficientData':
        return 'bg-secondary';
      default:
        return 'bg-light text-dark border';
    }
  }

  public overallLabel(overall: string): string {
    switch (overall) {
      case 'NotMonitored':
        return 'Not monitored';
      case 'InsufficientData':
        return 'Insufficient data';
      default:
        return overall;
    }
  }

  public statusLabel(status: string): string {
    return status === 'InsufficientData' ? 'No data' : status;
  }

  public failurePercent(row: DealerHealthRow): string {
    const rate = row.priceCheck.technicalFailureRate;
    if (rate === null || rate === undefined) return '—';
    return `${Math.round(rate * 100)}%`;
  }
}
